import type { CSSProperties } from "react";
import {
  CSS_MAX_CONTENT,
  CSS_POINTER_EVENTS_AUTO,
  CSS_POINTER_EVENTS_NONE,
  CSS_POSITION_FIXED,
  CSS_TRANSLATE_ABOVE_Y,
  CSS_TRANSLATE_CENTER_X,
  CSS_TRANSLATE_NONE,
  KEY_ESCAPE,
  TOOLTIP_EXIT_OFFSET_PX,
  TOOLTIP_PORTAL_Z_INDEX,
  TOOLTIP_VERTICAL_OFFSET_PX,
  TOOLTIP_VIEWPORT_PADDING_PX,
} from "./ctooltip-constants";
import { CtooltipPlacement, type TooltipPosition } from "./Ctooltip.types";

interface TooltipAnchorInput {
  anchorRect: DOMRect;
}

interface TooltipPlacementInput {
  placement: CtooltipPlacement;
}

interface GetTooltipIsOpenInput {
  visible: boolean;
  anchorElement: HTMLElement | null;
}

interface BuildTooltipPositionInput extends TooltipPlacementInput {
  anchorRect: DOMRect;
  viewportWidth: number;
  width?: number;
}

interface GetTooltipClampedWidthInput {
  viewportWidth: number;
  width: number;
}

interface GetTooltipClampedLeftInput {
  anchorCenterX: number;
  clampedWidth: number;
  viewportWidth: number;
}

interface GetTooltipViewportMaxWidthInput {
  vw: number;
}

interface GetTooltipPositionerStyleInput extends TooltipPlacementInput {
  hasDismissHandler: boolean;
  position: TooltipPosition;
  viewportMaxWidth: number;
}

interface GetTooltipTransformInput extends TooltipPlacementInput {
  position: TooltipPosition;
}

interface GetTooltipShouldStartAutoDismissTimerInput {
  autoDismissMs?: number;
  hasDismissHandler: boolean;
  isHovered: boolean;
  isOpen: boolean;
  pauseAutoDismissOnHover: boolean;
}

interface GetTooltipTargetWithinSurfaceInput {
  anchorElement: HTMLElement | null;
  target: Node;
  tooltipElement: HTMLElement | null;
}

interface GetShouldDismissTooltipFromMouseEventInput {
  anchorElement: HTMLElement | null;
  event: MouseEvent;
  tooltipElement: HTMLElement | null;
}

interface GetShouldDismissTooltipFromKeyInput {
  key: string;
}

type TooltipHoverAutoDismissState = "paused" | "running";
type TooltipAnchorEdgeResolver = (input: TooltipAnchorInput) => number;

const TOOLTIP_ANCHOR_EDGE_Y_BY_PLACEMENT: Record<
  CtooltipPlacement,
  TooltipAnchorEdgeResolver
> = {
  [CtooltipPlacement.Below]: ({ anchorRect }): number =>
    anchorRect.bottom + TOOLTIP_VERTICAL_OFFSET_PX,
  [CtooltipPlacement.Above]: ({ anchorRect }): number =>
    anchorRect.top - TOOLTIP_VERTICAL_OFFSET_PX,
};

const TOOLTIP_EXIT_Y_OFFSET_BY_PLACEMENT: Record<CtooltipPlacement, number> = {
  [CtooltipPlacement.Below]: -TOOLTIP_EXIT_OFFSET_PX,
  [CtooltipPlacement.Above]: TOOLTIP_EXIT_OFFSET_PX,
};

const TOOLTIP_TRANSLATE_Y_BY_PLACEMENT: Record<CtooltipPlacement, string> = {
  [CtooltipPlacement.Below]: CSS_TRANSLATE_NONE,
  [CtooltipPlacement.Above]: CSS_TRANSLATE_ABOVE_Y,
};

const TOOLTIP_HOVER_AUTO_DISMISS_STATE_BY_PAUSED_FLAG: Record<
  string,
  TooltipHoverAutoDismissState
> = {
  true: "paused",
  false: "running",
};

const TOOLTIP_AUTO_DISMISS_ENABLED_BY_HOVER_STATE: Record<
  TooltipHoverAutoDismissState,
  boolean
> = {
  paused: false,
  running: true,
};

export function getTooltipIsOpen({
  visible,
  anchorElement,
}: GetTooltipIsOpenInput): boolean {
  return visible && Boolean(anchorElement);
}

export function buildTooltipPosition({
  anchorRect,
  placement,
  viewportWidth,
  width,
}: BuildTooltipPositionInput): TooltipPosition {
  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const anchorEdgeY = TOOLTIP_ANCHOR_EDGE_Y_BY_PLACEMENT[placement]({
    anchorRect,
  });

  if (width == null) {
    return {
      leftOrCenterX: anchorCenterX,
      anchorEdgeY,
      fixedWidthPx: null,
    };
  }

  const clampedWidth = getTooltipClampedWidth({ viewportWidth, width });

  return {
    leftOrCenterX: getTooltipClampedLeft({
      anchorCenterX,
      clampedWidth,
      viewportWidth,
    }),
    anchorEdgeY,
    fixedWidthPx: clampedWidth,
  };
}

export function getTooltipViewportMaxWidth({
  vw,
}: GetTooltipViewportMaxWidthInput): number {
  return vw * 100 - TOOLTIP_VIEWPORT_PADDING_PX * 2;
}

export function getTooltipExitYOffset({
  placement,
}: TooltipPlacementInput): number {
  return TOOLTIP_EXIT_Y_OFFSET_BY_PLACEMENT[placement];
}

export function getTooltipPositionerStyle({
  hasDismissHandler,
  placement,
  position,
  viewportMaxWidth,
}: GetTooltipPositionerStyleInput): CSSProperties {
  return {
    position: CSS_POSITION_FIXED,
    left: position.leftOrCenterX,
    top: position.anchorEdgeY,
    transform: getTooltipTransform({ placement, position }),
    width: getTooltipWidthStyle({ position }),
    maxWidth: viewportMaxWidth,
    pointerEvents: hasDismissHandler
      ? CSS_POINTER_EVENTS_AUTO
      : CSS_POINTER_EVENTS_NONE,
    zIndex: TOOLTIP_PORTAL_Z_INDEX,
  };
}

export function getTooltipShouldStartAutoDismissTimer({
  autoDismissMs,
  hasDismissHandler,
  isHovered,
  isOpen,
  pauseAutoDismissOnHover,
}: GetTooltipShouldStartAutoDismissTimerInput): boolean {
  const hasAutoDismissDelay = autoDismissMs != null;
  const hasTimerPrerequisites = [
    isOpen,
    hasDismissHandler,
    hasAutoDismissDelay,
  ].every(Boolean);
  const hoverAutoDismissState = getTooltipHoverAutoDismissState({
    isHovered,
    pauseAutoDismissOnHover,
  });

  return (
    hasTimerPrerequisites &&
    TOOLTIP_AUTO_DISMISS_ENABLED_BY_HOVER_STATE[hoverAutoDismissState]
  );
}

export function getShouldDismissTooltipFromMouseEvent({
  anchorElement,
  event,
  tooltipElement,
}: GetShouldDismissTooltipFromMouseEventInput): boolean {
  const target = event.target as Node | null;

  if (!target) {
    return false;
  }

  return !getTooltipTargetWithinSurface({
    anchorElement,
    target,
    tooltipElement,
  });
}

export function getShouldDismissTooltipFromKey({
  key,
}: GetShouldDismissTooltipFromKeyInput): boolean {
  return key === KEY_ESCAPE;
}

function getTooltipClampedWidth({
  viewportWidth,
  width,
}: GetTooltipClampedWidthInput): number {
  const viewportInteriorWidth = viewportWidth - TOOLTIP_VIEWPORT_PADDING_PX * 2;

  return Math.min(width, Math.max(viewportInteriorWidth, 0));
}

function getTooltipClampedLeft({
  anchorCenterX,
  clampedWidth,
  viewportWidth,
}: GetTooltipClampedLeftInput): number {
  const halfWidth = clampedWidth / 2;
  const rawLeft = anchorCenterX - halfWidth;
  const minLeft = TOOLTIP_VIEWPORT_PADDING_PX;
  const maxLeft = viewportWidth - clampedWidth - TOOLTIP_VIEWPORT_PADDING_PX;

  return Math.min(Math.max(rawLeft, minLeft), Math.max(maxLeft, minLeft));
}

function getTooltipTransform({
  placement,
  position,
}: GetTooltipTransformInput): string {
  return `translate(${getTooltipTranslateX({ position })}, ${TOOLTIP_TRANSLATE_Y_BY_PLACEMENT[placement]})`;
}

function getTooltipTranslateX({
  position,
}: Pick<GetTooltipTransformInput, "position">): string {
  if (position.fixedWidthPx == null) {
    return CSS_TRANSLATE_CENTER_X;
  }

  return CSS_TRANSLATE_NONE;
}

function getTooltipWidthStyle({
  position,
}: Pick<GetTooltipTransformInput, "position">): CSSProperties["width"] {
  if (position.fixedWidthPx == null) {
    return CSS_MAX_CONTENT;
  }

  return `${position.fixedWidthPx}px`;
}

function getTooltipHoverAutoDismissState({
  isHovered,
  pauseAutoDismissOnHover,
}: Pick<
  GetTooltipShouldStartAutoDismissTimerInput,
  "isHovered" | "pauseAutoDismissOnHover"
>): TooltipHoverAutoDismissState {
  return TOOLTIP_HOVER_AUTO_DISMISS_STATE_BY_PAUSED_FLAG[
    String(pauseAutoDismissOnHover && isHovered)
  ];
}

function getTooltipTargetWithinSurface({
  anchorElement,
  target,
  tooltipElement,
}: GetTooltipTargetWithinSurfaceInput): boolean {
  return Boolean(
    anchorElement?.contains(target) || tooltipElement?.contains(target),
  );
}
